import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import AccountTable from '../../shared/components/AccountManagement/AccountTable'
import { Grid } from '@mui/material'
import { UserLoginSortOrder, UserLoginSortValue } from '@professocialApi/professocial-core-frontend-api'
import { useDispatch, useSelector } from 'react-redux'
import { Redirect, Route, Switch, useHistory, useLocation, useRouteMatch } from 'react-router'
import {
  CreateUserAccountRequest,
  DeleteUserAccountRequest,
  GetAllUsersRequest,
  UpdateUserAccountRequest
} from '@professocialApi/professocial-core-frontend-api/apis/UserLoginRESTServiceApi'
import {
  createUserAccountInitiated,
  deleteUserAccountInitiated, forceDisconnectInitiated,
  getAllUsersInitiated,
  listSubsidiariesInitiated,
  setOnSuccess,
  updateUserAccountInitiated
} from '../../store/accounts/accounts.actions'
import { ListSubsidiariesRequest } from '@professocialApi/professocial-core-frontend-api/apis/SubsidiaryRESTServiceApi'
import { clearRequests } from '../../store/common/common.actions'
import { SaveSuccess } from '../../store/accounts/accounts.reducer'
import { ResultPageWebModel, Subsidiary, UserSettings } from '@professocialApi/professocial-core-frontend-api/models'
import { getAllSubsidiaries, getAllUserAccounts } from '../../store/accounts/accounts.selector'
import AccountEdit from '../../shared/components/AccountManagement/AccountEdit'
import DialogComponent from '@professocial/professocialFormComponents/FormComponents/DialogComponent'
import { BASE_PATH } from '../../config/envConfig'
import DropdownComponent, {
  DropDownElement,
} from '@professocial/professocialFormComponents/FormComponents/DropDownComponent'
import { ActionCall } from '@professocial/professocialTypes/types'
import { RootState } from '../../store/accountRoot.reducer'
import { namedRequestData } from '../../store/common/common.selectors'
import { Mandator } from '@professocialApi/professocial-core-frontend-api/models/Mandator'

export type FilterParams = {
	limit: number
	offset: number
	sortOrder: UserLoginSortOrder
	sortValue?: UserLoginSortValue | undefined
	searchWith?: string
}

export enum HiddenAttr {
	xlUp = 'xlUp',
	lgUp = 'lgUp',
	mdUp = 'mdUp',
	smUp = 'smUp',
	xsUp = 'xsUp',
}

export type Template = {
	name: string
	hidden?: HiddenAttr
	sortable?: UserLoginSortValue
	label?: string
	format?: string
}

export type TableTemplate = {
	[key: string]: any
	user: Template[]
	detainee: Template[]
}

interface IProps {
	user?: UserSettings;
}

const AdminAccounts: React.FC<IProps> = (props) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { path } = useRouteMatch()
  const location = useLocation()
  const history = useHistory()

  const xsrfToken = sessionStorage.getItem('XSRF-TOKEN')
  const requestHeaders: HeadersInit = new Headers()
  requestHeaders.set('Accept', 'application/json')
  requestHeaders.set('Content-Type', 'application/json')
  if (xsrfToken) requestHeaders.set('X-XSRF-TOKEN', xsrfToken)

  const dispatchGetAllUsers = useCallback((request: GetAllUsersRequest) => dispatch(getAllUsersInitiated(request)), [dispatch])
  const dispatchListSubsidiaries = useCallback((request: ListSubsidiariesRequest) => dispatch(listSubsidiariesInitiated(request)), [dispatch])
  const dispatchCreateUserAccount = useCallback((request: CreateUserAccountRequest) => dispatch(createUserAccountInitiated(request)), [dispatch])
  const dispatchDeleteUserAccount = useCallback((request: DeleteUserAccountRequest) => dispatch(deleteUserAccountInitiated(request)), [dispatch])
  const dispatchUpdateUserAccount = useCallback((request: UpdateUserAccountRequest) => dispatch(updateUserAccountInitiated(request)), [dispatch])
  const dispatchSaveSuccess = useCallback((status: SaveSuccess | null) => dispatch(setOnSuccess(status)), [dispatch])
  const dispatchClearRequests = useCallback(() => dispatch(clearRequests()), [dispatch])

  const allUserAccounts: ResultPageWebModel | undefined = useSelector(getAllUserAccounts)
  const allSubsidiaries: Subsidiary[] | undefined = useSelector(getAllSubsidiaries)

  const defaultDateFormat = 'dd.MM.yyyy'
  const defaultTimeFormat = 'HH:mm'

  const [toast, setToast] = React.useState<any>()

  const tableTemplate: TableTemplate = {
    user: [
      {
        name: 'username',
        hidden: undefined,
        sortable: UserLoginSortValue.Username,
        label: 'username',
      },
      {
        name: 'email',
        hidden: HiddenAttr.smUp,
        sortable: UserLoginSortValue.Email,
        label: 'email',
      },
      {
        name: 'lastName, firstName',
        hidden: HiddenAttr.mdUp,
        sortable: UserLoginSortValue.LastName,
        label: 'name',
      },
      {
        name: 'createdAt',
        hidden: HiddenAttr.mdUp,
        sortable: UserLoginSortValue.CreatedAt,
        label: 'createdAt',
        format: `${defaultDateFormat}, ${defaultTimeFormat}`,
      },
      {
        name: 'lastLogin',
        hidden: HiddenAttr.lgUp,
        sortable: UserLoginSortValue.LastLogin,
        label: 'lastLogin',
        format: `${defaultDateFormat}, ${defaultTimeFormat}`,
      },
      {
        name: 'subsidiary.label',
        hidden: HiddenAttr.lgUp,
        // sortable: 'subsidiary',
        label: 'subsidiary',
      }],
    subsidiary: [
      {
        name: 'username',
        hidden: null,
        sortable: UserLoginSortValue.Username,
        label: 'username',
      },
      {
        name: 'email',
        hidden: HiddenAttr.smUp,
        sortable: UserLoginSortValue.Email,
        label: 'email',
      },
      {
        name: 'lastName, firstName',
        hidden: HiddenAttr.mdUp,
        sortable: UserLoginSortValue.LastName,
        label: 'name',
      },
      {
        name: 'lastLogin',
        hidden: HiddenAttr.lgUp,
        sortable: UserLoginSortValue.LastLogin,
        label: 'lastLogin',
        format: `${defaultDateFormat}, ${defaultTimeFormat}`,
      },
      {
        name: 'subsidiary.label',
        hidden: HiddenAttr.lgUp,
        // sortable: 'subsidiary',
        label: 'subsidiary',
      },
      {
        name: 'mandator.label',
        hidden: HiddenAttr.lgUp,
        sortable: UserLoginSortValue.Mandator,
        label: 'mandator',
      },
    ],
    editor: [
      {
        name: 'username',
        hidden: null,
        sortable: UserLoginSortValue.Username,
        label: 'username',
      },
      {
        name: 'email',
        hidden: HiddenAttr.smUp,
        sortable: UserLoginSortValue.Email,
        label: 'email',
      },
      {
        name: 'lastName, firstName',
        hidden: HiddenAttr.mdUp,
        sortable: UserLoginSortValue.LastName,
        label: 'name',
      },
      {
        name: 'createdAt',
        hidden: HiddenAttr.mdUp,
        sortable: UserLoginSortValue.CreatedAt,
        label: 'createdAt',
        format: `${defaultDateFormat}, ${defaultTimeFormat}`,
      },
      {
        name: 'lastLogin',
        hidden: HiddenAttr.lgUp,
        sortable: UserLoginSortValue.LastLogin,
        label: 'lastLogin',
        format: `${defaultDateFormat}, ${defaultTimeFormat}`,
      },
      {
        name: 'mandator.label',
        hidden: HiddenAttr.lgUp,
        sortable: UserLoginSortValue.Mandator,
        label: 'mandator',
      },
    ],
    admin: [
      {
        name: 'username',
        hidden: null,
        sortable: UserLoginSortValue.Username,
        label: 'username',
      },
      {
        name: 'email',
        hidden: HiddenAttr.smUp,
        sortable: UserLoginSortValue.Email,
        label: 'email',
      },
      {
        name: 'lastName, firstName',
        hidden: HiddenAttr.mdUp,
        sortable: UserLoginSortValue.LastName,
        label: 'name',
      },
      {
        name: 'createdAt',
        hidden: HiddenAttr.mdUp,
        sortable: 'createdAt',
        label: UserLoginSortValue.CreatedAt,
        format: `${defaultDateFormat}, ${defaultTimeFormat}`,
      },
      {
        name: 'lastLogin',
        hidden: HiddenAttr.lgUp,
        sortable: UserLoginSortValue.LastLogin,
        label: 'lastLogin',
        format: `${defaultDateFormat}, ${defaultTimeFormat}`,
      },
      {
        name: 'mandator.label',
        hidden: HiddenAttr.lgUp,
        sortable: 'mandator',
        label: 'mandator',
      },
    ],
    detainee: [
      {
        name: 'username',
        hidden: undefined,
        sortable: UserLoginSortValue.Username,
        label: 'username',
      },
      {
        name: 'email',
        hidden: HiddenAttr.smUp,
        sortable: UserLoginSortValue.Email,
        label: 'email',
      },
      {
        name: 'lastName, firstName',
        hidden: HiddenAttr.mdUp,
        sortable: UserLoginSortValue.LastName,
        label: 'name',
      },
      {
        name: 'createdAt',
        hidden: HiddenAttr.mdUp,
        sortable: UserLoginSortValue.CreatedAt,
        label: 'createdAt',
        format: `${defaultDateFormat}, ${defaultTimeFormat}`,
      },
      {
        name: 'lastLogin',
        hidden: HiddenAttr.lgUp,
        sortable: UserLoginSortValue.LastLogin,
        label: 'lastLogin',
        format: `${defaultDateFormat}, ${defaultTimeFormat}`,
      },
      {
        name: 'mandator.label',
        hidden: HiddenAttr.lgUp,
        sortable: UserLoginSortValue.Mandator,
        label: 'mandator',
      },
    ],
  }
  const [role, setRole] = React.useState<string | null>('user')
  const [subsidiaries, setSubsidiaries] = useState<Subsidiary[]>([])
  const [mandators, setMandators] = useState()
  const [accountQueryParams, setAccountQueryParams] = React.useState<FilterParams>({
    limit: 25,
    offset: 0,
    sortOrder: UserLoginSortOrder.Desc,
  })

  const actionOnUserCreate: ActionCall | undefined = useSelector((state: RootState) => namedRequestData(state, createUserAccountInitiated.type))
  const actionOnUserUpdate: ActionCall | undefined = useSelector((state: RootState) => namedRequestData(state, updateUserAccountInitiated.type))
  const actionOnUserDelete: ActionCall | undefined = useSelector((state: RootState) => namedRequestData(state, deleteUserAccountInitiated.type))
  const actionOnPageDisconnect: ActionCall | undefined = useSelector((state: RootState) => namedRequestData(state, forceDisconnectInitiated.type))

  useEffect(() => {
    if (actionOnUserDelete?.status === 'success' || actionOnPageDisconnect?.status === 'success') {
      loadData()
    }

    if (actionOnUserCreate?.status === 'success' || actionOnUserUpdate?.status === 'success') {
      history.goBack()
      loadData()
    }

    if (actionOnUserCreate?.status === 'failed' && actionOnUserCreate?.error) {
      setToast({
        autoClose: false,
        open: true,
        type: 'error',
        title: t('topicsMessages:error'),
        message: t('accountsCommon:cannotCreateUser'),
        severity: 'error',
        requestActions: true,
        cancelLabel: null,
        confirmLabel: t('topicsCommon:ok'),
        handleClose: () => setToast(null),
        handleConfirm: () => {
          setToast(null)
          dispatchClearRequests()
        },
      })
    }
  }, [actionOnUserUpdate, actionOnUserCreate, actionOnUserDelete, actionOnPageDisconnect])

  useEffect(() => {
    if (allSubsidiaries) {
      setSubsidiaries(allSubsidiaries)
    }
  }, [allSubsidiaries])

  useEffect(() => {
    loadData()
  }, [accountQueryParams, role])

  useEffect(() => {
    loadSubsidiaryData()
  }, [])

  useEffect(() => {
    loadSubsidiaryData()
    loadMandatorData()
  }, [])

  useEffect(() => {
    const pathname = location.pathname
    const path = pathname.split('/')
    const key = filterOptions[0]
    if (path[2] !== 'unlinkedPages') {
      key && key && setRole(key.key)
    } else {
      setRole(null)
    }
  }, [])

  const handleMessage = (data: any) => {
    setToast(data)
  }

  const loadMandatorData = () => {
    const restUrl = `${BASE_PATH}/rest/professocial/core/stewardship/mandator/`
    fetch(restUrl,
      {
        method: 'GET',
        credentials: 'include',
        headers: requestHeaders,
      })
      .then(res => res.json())
      .then(json => {
        setMandators(json)
      })
  }

  const loadSubsidiaryData = () => {
    if (props.user && props.user.mandator?.id) {
      const request: ListSubsidiariesRequest = {
        mandatorId: props.user.mandator.id,
      }
      dispatchListSubsidiaries(request)
    }
  }

  const loadData = () => {
    if (role) {
      const requestParams: GetAllUsersRequest = {
        role: role,
        limit: accountQueryParams.limit,
        offset: accountQueryParams.offset,
        searchWith: accountQueryParams.searchWith,
      }
      if (accountQueryParams.sortValue) {
        requestParams.sortValue = accountQueryParams.sortValue
        requestParams.sortOrder = accountQueryParams.sortOrder
      }
      dispatchGetAllUsers(requestParams)
    }
    dispatchSaveSuccess(null)
  }

  const reloadData = (newParams: FilterParams) => {
    if (JSON.stringify(accountQueryParams) !== JSON.stringify(newParams)) {
      setAccountQueryParams(newParams)
    }
  }

  const deleteUserQuestion = (userId: string) => {
    setToast({
      autoClose: false,
      open: true,
      type: 'warning',
      title: t('networkContentMessages:deleteWarningTitle'),
      message: t('accountsCommon:userDeletingQuestion'),
      severity: 'error',
      requestActions: true,
      confirmLabel: t('networkContentCommon:delete'),
      cancelLabel: t('networkContentCommon:cancel'),
      handleClose: () => setToast(null),
      handleCancel: () => setToast(null),
      handleConfirm: () => {
        setToast(null)
        deleteUser(userId)
      },
    })
  }

  const deleteUser = (userId: string) => {
    const deleteRequest: DeleteUserAccountRequest = {
      userId: userId,
    }
    dispatchDeleteUserAccount(deleteRequest)
  }

  const createNewUser = (data: any) => {
    const createRequest: CreateUserAccountRequest = {
      userLogin: data,
    }
    dispatchCreateUserAccount(createRequest)
  }

  const resetFailedLoginCount = (data: any) => {
    const updateRequest: UpdateUserAccountRequest = {
      userId: data.id,
      userLogin: data,
    }
    dispatchUpdateUserAccount(updateRequest)
  }

  const changeUserData = (data: any) => {
    const updateRequest: UpdateUserAccountRequest = {
      userId: data.id,
      userLogin: data,
    }
    dispatchUpdateUserAccount(updateRequest)
  }

  const filterOptions: DropDownElement[] = [
    {
      key: 'user',
      value: t('accountsCommon:user'),
    },
    {
      key: 'subsidiary',
      value: t('accountsCommon:subsidiaryManagement'),
    },
    {
      key: 'editor',
      value: t('accountsCommon:editor'),
    },
    {
      key: 'admin',
      value: t('accountsCommon:admin'),
    },
    {
      key: 'detainee',
      value: t('accountsCommon:detainee'),
    },
  ]

  const csvExport = () => {
    if (props.user?.role === 'ADMIN') {
      if (role === 'detainee') {
        window.open(
          `${BASE_PATH}/rest/professocial/core/gatekeeper/statistic/current/mandator/detainees`,
          'csv',
        )
      } else {
        window.open(
          `${BASE_PATH}/rest/professocial/core/gatekeeper/statistic/current/mandator/users`,
          'csv',
        )
      }
    }
  }
  return (
    <Grid container style={{ margin: '25px 0px' }}>
      <Grid xs={12} item container direction="row">
        <Grid container item xs={12}>
          <Switch>
            <Redirect exact from={`${path}`} to={`${path}/accounts`}/>
            <Redirect exact from={`${path}/accounts//*`} to={`${path}/accounts/*`}/>.
            <Route path={`${path}/accounts/edit/:id`}>
              <AccountEdit
                user={props.user}
                subsidiaries={subsidiaries}
                mandators={mandators}
                role="ADMIN"
                data={allUserAccounts}
                onEdit
                handleMessage={handleMessage}
                changeUserData={(data: any) => changeUserData(data)}
                resetFailedLoginCount={(data: any) => resetFailedLoginCount(data)}
              />
            </Route>
            <Route path={`${path}/accounts/add_user`}>
              <AccountEdit
                user={props.user}
                subsidiaries={subsidiaries}
                mandators={mandators}
                role="ADMIN"
                createNewUser={(data: any) => createNewUser(data)}
              />
            </Route>
            <Route exact path={`${path}/accounts`}>
              {role && (
                <>
                  <Grid xs={3} sx={{ mb: 3 }} item container direction="row">
                    <DropdownComponent
                      options={filterOptions}
                      value={role}
                      label={t('accountsCommon:filter')}
                      size="small"
                      disableClearable
                      onChange={(option: DropDownElement) => {
                        setRole(option.key)
                      }}
                    />
                  </Grid>
                  <AccountTable
                    data={allUserAccounts}
                    filterParams={accountQueryParams}
                    template={(role && tableTemplate[role] ? tableTemplate[role] : tableTemplate.user) as Template[]}
                    user={props.user}
                    pageKey={role}
                    mandatorOptions={mandators && mandators[0] as Mandator}
                    csvExport={() => csvExport()}
                    deleteUser={(userId: string) => deleteUserQuestion(userId)}
                    setFilterSettings={reloadData}
                  />
                </>
              )}
            </Route>
          </Switch>
          {toast && (
            <DialogComponent
              type={toast.type || undefined}
              autoClose={toast.autoClose}
              title={toast.title}
              message={toast.message}
              open={toast.open}
              confirmLabel={toast.confirmLabel}
              cancelLabel={toast.cancelLabel}
              requestActions={toast.requestActions}
              onClose={toast.handleClose}
              onCancel={toast.handleCancel || undefined}
              onConfirm={toast.handleConfirm || undefined}
            />
          )}
        </Grid>
      </Grid>
    </Grid>
  )
}
export default AdminAccounts
